import * as React from "react";
import EndTurnSummary from "src/components/EndTurnSummary";
import { EditableTextArea } from "src/components/shared/Editable";
import { displayErrorToast } from "src/components/shared/Toasts/Toast";
import {
  ShowCardsButton,
  TaskCard,
  TaskCardLabel,
  TaskName,
} from "src/components/Table/TaskBoard/TaskBoard.components";
import { ToastIds } from "src/enums/errorToastIds";
import { useGameContext } from "src/game/useGameContext";

const RevealTaskBoard: React.FunctionComponent = () => {
  const { gameState, actionHandlers } = useGameContext();

  const [isInputDisabled, setIsInputDisabled] = React.useState(true);
  const [editableTask, setEditableTask] = React.useState(
    gameState.game?.task?.description || ""
  );
  const task = gameState.game?.task;

  React.useEffect(() => {
    if (!isInputDisabled && !task) {
      setEditableTask("");
    } else if (isInputDisabled && editableTask == "" && !task) {
      setEditableTask("Add a new task");
    }
  }, [isInputDisabled]);

  React.useEffect(() => {
    if (!task) {
      setEditableTask("Add a new task");
      return;
    }
    setEditableTask(task.description);
  }, [gameState.game?.task]);

  const handleBlur = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setIsInputDisabled(true);

    //try to change task description
    if (!gameState.game?.id || !gameState.token) {
      return;
    }

    if (!editableTask.length || editableTask.length > 255) {
      const msg = `Task description is too ${
        !editableTask.length ? "short" : "long"
      }`;

      const errId = !editableTask.length
        ? ToastIds.TASK_DESCRIPTION_TOO_SHORT
        : ToastIds.TASK_DESCRIPTION_TOO_LONG;

      displayErrorToast(msg, errId);
      setIsInputDisabled(false);
      setTimeout(() => {
        e.target.select();
      }, 0);
      setEditableTask(task?.description || "");
      return;
    }

    const authData = {
      gameId: gameState.game?.id,
      token: gameState.token,
    };
    const args = {
      ...authData,
      task: {
        description: editableTask,
      },
    };

    try {
      const response = task
        ? await actionHandlers.updateTask({
            taskId: task.id,
            ...args,
          })
        : await actionHandlers.addTask(args);

      if (response.error) {
        displayErrorToast(
          response.error.message,
          task ? ToastIds.TASK_UPDATE_FAILED : ToastIds.TASK_CREATE_FAILED
        );
        return;
      }
    } catch {
      displayErrorToast("An unknown error occurred");
    }
  };

  const handleClick = async () => {
    if (!gameState.game?.id || !gameState.token) {
      return;
    }

    const authData = {
      gameId: gameState.game?.id,
      token: gameState.token,
    };

    //reveal votes
    if (!gameState?.voteReveal) {
      try {
        const response = await actionHandlers.revealVotes(authData);
        if (response.error) {
          displayErrorToast(
            `Failed to reveal votes ${response.error.message}`,
            ToastIds.REVEAL_VOTES_FAILED
          );
        }
      } catch (e) {
        displayErrorToast(
          "Failed to reveal votes",
          ToastIds.REVEAL_VOTES_FAILED
        );
      }
    } else {
      //start new voting
      try {
        const response = await actionHandlers.startNewVoting({
          ...authData,
          taskId: null,
        });
        if (response.error) {
          displayErrorToast(
            `Failed to start a new vote ${response.error.message}`,
            ToastIds.NEW_VOTING_FAILED
          );
        }
      } catch (e) {
        displayErrorToast(
          "Failed to start a new vote",
          ToastIds.NEW_VOTING_FAILED
        );
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.currentTarget;
    setEditableTask(value.replace(/(\r\n|\n|\r)/gm, ""));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      e.target.dispatchEvent(new Event("submit"));
      e.preventDefault();
    }
  };

  return (
    <>
      {gameState?.voteReveal ? (
        <TaskName data-cy="taskName" isInactive>
          {task?.description}
        </TaskName>
      ) : (
        <EditableTextArea
          isDisabled={isInputDisabled}
          data-cy="taskNameInput"
          isDisabledSetter={setIsInputDisabled}
          editableValue={editableTask}
          onInput={handleChange}
          onKeyPress={handleKeyPress}
          onBlurCapture={handleBlur}
          isTaskCard={true}
          numOfChars={editableTask.length + 1}
          onSubmitCapture={handleBlur}
        />
      )}

      {!gameState?.voteReveal?.finalResult ? (
        <EndTurnSummary />
      ) : (
        <>
          <TaskCard>{gameState?.voteReveal.finalResult}</TaskCard>
          <TaskCardLabel>Final result</TaskCardLabel>
        </>
      )}

      {(!gameState?.voteReveal || gameState?.voteReveal.finalResult) &&
      !!gameState.game?.task ? (
        <ShowCardsButton
          data-cy="showCardsButton"
          themeColor="buttonPrimary"
          onClick={handleClick}>
          {gameState?.voteReveal ? "Next voting" : "Show cards"}
        </ShowCardsButton>
      ) : null}
    </>
  );
};

export default RevealTaskBoard;
